import React, { useEffect, useState, useRef, Suspense, lazy, createElement } from 'react';
import { motion } from 'framer-motion';
import { Check, BookOpen, Users, Clock, Download, Shield, MessageCircle, Star, GraduationCap, Backpack, Headphones, FileText, BookMarked, ChevronLeft, ChevronRight } from 'lucide-react';
import { TestimonialCard } from './components/TestimonialCard';
// Preload critical hero image
const heroImageUrl = 'https://cdn.magicpatterns.com/uploads/pHKNYTP6qPYtrNBkwEXadn/pasted-image.png';
const materialImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/58fXu8QW38pez3jHutS97T/Captura_de_pantalla_2026-02-01_a_la(s)_5.36.59_p._m..png',
  alt: 'Manual 01 - Índice de contenidos',
  label: 'Manual 01 - Índice'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kEVP2iUz6ke2RMueYVRnG7/Captura_de_pantalla_2026-02-01_a_la(s)_5.38.08_p._m..png',
  alt: 'Manual 02 - Índice de contenidos',
  label: 'Manual 02 - Índice'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oGkP9ruopRsb7D3ngBoV1E/Captura_de_pantalla_2026-02-01_a_la(s)_5.38.37_p._m..png',
  alt: 'Manual 03 - Índice de contenidos',
  label: 'Manual 03 - Índice'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iefXTz1y683eACN2v6jUam/Captura_de_pantalla_2026-02-01_a_la(s)_5.37.14_p._m..png',
  alt: 'Verb to be - Explicación',
  label: 'Teoría: Verb to be'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aCfZYhVX8dHRxeDbuuEwf5/Captura_de_pantalla_2026-02-01_a_la(s)_5.38.21_p._m..png',
  alt: 'Adjectives - Explicación',
  label: 'Teoría: Adjectives'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/bgzCiW4wRfeQmnnHSGM76A/Captura_de_pantalla_2026-02-01_a_la(s)_5.38.48_p._m..png',
  alt: 'Simple Present - Explicación',
  label: 'Teoría: Simple Present'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5QY8EuxwnryH9Eph1QRLGY/Captura_de_pantalla_2026-02-01_a_la(s)_5.37.47_p._m..png',
  alt: 'Demonstrative Pronouns - Worksheet',
  label: 'Worksheet: Pronouns'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iBWGmLoVeMyd8oBffKkPDU/Captura_de_pantalla_2026-02-01_a_la(s)_5.37.54_p._m..png',
  alt: 'Demonstrative Pronouns - Ejercicios',
  label: 'Ejercicios: Pronouns'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/unSwiwDfZ4RkoHc9iUKKhP/Captura_de_pantalla_2026-02-01_a_la(s)_5.39.09_p._m..png',
  alt: 'Second Conditional - Portada',
  label: 'Portada de Unidad'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kAPc4SyX2zQXGVC4t3za5a/Captura_de_pantalla_2026-02-01_a_la(s)_5.39.17_p._m..png',
  alt: 'Worksheet - Second Conditional',
  label: 'Worksheet: Conditional'
}];
function MaterialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Lazy load carousel when visible
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '200px'
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);
  const scrollToIndex = (index: number) => {
    if (carouselRef.current) {
      const scrollWidth = carouselRef.current.scrollWidth;
      const itemWidth = scrollWidth / materialImages.length;
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };
  const handlePrev = () => {
    const newIndex = currentIndex === 0 ? materialImages.length - 1 : currentIndex - 1;
    scrollToIndex(newIndex);
  };
  const handleNext = () => {
    const newIndex = currentIndex === materialImages.length - 1 ? 0 : currentIndex + 1;
    scrollToIndex(newIndex);
  };
  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.scrollWidth / materialImages.length;
      const newIndex = Math.round(scrollLeft / itemWidth);
      if (newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
      }
    }
  };
  return <div className="relative" ref={containerRef} data-id="element-0">
      {/* Navigation Buttons */}
      <button onClick={handlePrev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" aria-label="Anterior" data-id="element-1">
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" data-id="element-2" />
      </button>

      <button onClick={handleNext} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" aria-label="Siguiente" data-id="element-3">
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" data-id="element-4" />
      </button>

      {/* Carousel Container */}
      <div ref={carouselRef} onScroll={handleScroll} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-2" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-5">
        {materialImages.map((image, idx) => <div key={idx} className="flex-shrink-0 snap-center" data-id="element-6">
            <div className="w-64 md:w-80 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100" data-id="element-7">
              <div className="aspect-[3/4] overflow-hidden bg-gray-100" data-id="element-8">
                {isVisible && <img src={image.src} alt={image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" data-id="element-9" />}
              </div>
              <div className="p-3 text-center" data-id="element-10">
                <p className="text-sm font-medium text-gray-700" data-id="element-11">
                  {image.label}
                </p>
              </div>
            </div>
          </div>)}
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-6" data-id="element-12">
        {materialImages.map((_, idx) => <button key={idx} onClick={() => scrollToIndex(idx)} className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${idx === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Ir a imagen ${idx + 1}`} data-id="element-13" />)}
      </div>
    </div>;
}
export function App() {
  useEffect(() => {
    // Defer Facebook Pixel loading to not block initial render
    const loadFacebookPixel = () => {
      ;
      (function (f: any, b: Document, e: string, v: string, n: any, t: any, s: any) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          b.head.appendChild(t);
        }
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      (window as any).fbq('init', '777405255323572');
      (window as any).fbq('track', 'PageView');
      if (document.body) {
        const noscript = document.createElement('noscript');
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = 'https://www.facebook.com/tr?id=777405255323572&ev=PageView&noscript=1';
        noscript.appendChild(img);
        document.body.appendChild(noscript);
      }
    };
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadFacebookPixel);
    } else {
      setTimeout(loadFacebookPixel, 100);
    }
  }, []);
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-14">
      {/* HERO SECTION */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-8 md:py-12 px-4" data-id="element-15">
        <div className="max-w-4xl mx-auto text-center" data-id="element-16">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-17">
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-medium text-sm mb-4" data-id="element-18">
              🇬🇧📘 MEGAPACK INGLÉS LISTO™
            </span>

            <h1 className="text-xl md:text-3xl font-bold text-[#1E3A8A] leading-snug mb-3 max-w-2xl mx-auto" data-id="element-19">
              Todo el material de Inglés que necesitás para dar clases durante
              todo el año,{' '}
              <span className="text-blue-600" data-id="element-20">ya listo para usar.</span>
            </h1>

            {/* Imagen principal del MEGAPACK - Critical, loads first */}
            <div className="mb-3" data-id="element-21">
              <img src={heroImageUrl} alt="MEGAPACK Inglés Listo - Material completo para enseñar inglés" className="w-56 md:w-72 mx-auto drop-shadow-2xl" fetchPriority="high" decoding="async" data-id="element-22" />
            </div>

            <p className="text-lg md:text-xl text-gray-600 mb-5 max-w-2xl mx-auto leading-relaxed" data-id="element-23">
              Sin improvisar. Sin perder horas planificando. Sin volver a
              empezar desde cero.
            </p>

            <button onClick={scrollToPurchase} className="bg-[#2563EB] hover:bg-blue-700 text-white text-lg font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 mb-4" data-id="element-24">
              👉 ACCEDER AHORA AL MEGAPACK
            </button>

            <p className="text-sm text-gray-500 mb-8" data-id="element-25">
              Acceso inmediato · Producto digital
            </p>

            <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600" data-id="element-26">
              <div className="flex items-center gap-2" data-id="element-27">
                <div className="flex" data-id="element-28">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-29" />)}
                </div>
                <span data-id="element-30">Valoración promedio</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-31">
                <Users className="w-4 h-4 text-blue-600" data-id="element-32" />
                <span className="font-medium" data-id="element-33">+2.800 docentes ya lo usan</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-34">
        <div className="max-w-3xl mx-auto" data-id="element-35">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1E3A8A] mb-12" data-id="element-36">
            Si sos docente de Inglés, esto te va a sonar familiar…
          </h2>

          <div className="bg-gray-50 rounded-2xl p-8 md:p-10 mb-8" data-id="element-37">
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed" data-id="element-38">
              <p data-id="element-39">Llegás a tu casa cansado/a.</p>
              <p data-id="element-40">Sabés que tenés que planificar.</p>
              <p data-id="element-41">Abrís la compu…</p>
              <p className="font-semibold text-gray-900" data-id="element-42">
                y empezás otra vez desde cero.
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-43">
              <p className="text-gray-600" data-id="element-44">
                Buscando worksheets. Buscando actividades que funcionen.
                Buscando material por nivel. Buscando recursos que no te hagan
                perder tiempo.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 text-center" data-id="element-45">
            <p className="text-gray-700" data-id="element-46">
              <span className="font-semibold text-[#1E3A8A]" data-id="element-47">
                ❌ El problema no es que no sepas enseñar.
              </span>
              <br data-id="element-48" />
              El problema es que nadie te dio un sistema listo para usar.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-49">
        <div className="max-w-4xl mx-auto text-center" data-id="element-50">
          <p className="text-blue-600 font-semibold uppercase tracking-wide text-sm mb-4" data-id="element-51">
            La solución
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-6" data-id="element-52">
            🇬🇧 MEGAPACK INGLÉS LISTO™
          </h2>

          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-53">
            Esto no es un montón de PDFs sueltos.
          </p>

          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-54">
            Es un{' '}
            <span className="font-semibold" data-id="element-55">
              sistema completo de material de Inglés
            </span>
            , organizado por nivel y dificultad, para que puedas dar clases
            durante todo el año sin improvisar.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-56">
            {[{
            icon: Check,
            text: 'Todo listo'
          }, {
            icon: Check,
            text: 'Todo probado'
          }, {
            icon: Check,
            text: 'Todo ordenado'
          }, {
            icon: Check,
            text: 'Todo usable en clase real'
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center p-4 bg-white rounded-xl border border-gray-100" data-id="element-57">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3" data-id="element-58">
                  <item.icon className="w-5 h-5 text-green-600" data-id="element-59" />
                </div>
                <span className="font-medium text-gray-800 text-sm" data-id="element-60">
                  {item.text}
                </span>
              </div>)}
          </div>

          <button onClick={scrollToPurchase} className="bg-[#2563EB] hover:bg-blue-700 text-white text-lg font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200" data-id="element-61">
            👉 QUIERO ACCESO INMEDIATO
          </button>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-62">
        <div className="max-w-4xl mx-auto" data-id="element-63">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1E3A8A] mb-4" data-id="element-64">
            ¿Qué incluye el MEGAPACK?
          </h2>
          <p className="text-center text-gray-600 mb-12" data-id="element-65">
            Material completo para trabajar:
          </p>

          <div className="grid md:grid-cols-5 gap-4 mb-12" data-id="element-66">
            {['Gramática', 'Vocabulario', 'Comprensión lectora', 'Listening', 'Producción oral'].map((item, idx) => <div key={idx} className="bg-blue-50 rounded-xl p-4 text-center" data-id="element-67">
                <span className="font-medium text-[#1E3A8A]" data-id="element-68">{item}</span>
              </div>)}
          </div>

          <p className="text-center text-gray-600 mb-10" data-id="element-69">
            Todo con actividades listas, claras y adaptables al aula.
          </p>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-70">
            {[{
            icon: BookOpen,
            number: '+380',
            label: 'worksheets'
          }, {
            icon: FileText,
            number: '+1.000',
            label: 'ejercicios'
          }, {
            icon: BookMarked,
            number: '+80',
            label: 'hojas de teoría clara'
          }].map((item, idx) => <div key={idx} className="bg-gray-50 rounded-2xl p-6 text-center" data-id="element-71">
                <item.icon className="w-8 h-8 text-blue-600 mx-auto mb-3" data-id="element-72" />
                <p className="text-3xl font-bold text-[#1E3A8A]" data-id="element-73">
                  {item.number}
                </p>
                <p className="text-gray-600" data-id="element-74">{item.label}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* MATERIAL PREVIEW CAROUSEL */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-75">
        <div className="max-w-5xl mx-auto" data-id="element-76">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1E3A8A] mb-4" data-id="element-77">
            👀 Mirá el material por dentro
          </h2>
          <p className="text-center text-gray-600 mb-10" data-id="element-78">
            Así se ve el contenido que vas a recibir
          </p>

          <MaterialCarousel data-id="element-79" />
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-80">
        <div className="max-w-3xl mx-auto text-center" data-id="element-81">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-10" data-id="element-82">
            ¿Para quién es?
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-83">
            <div className="bg-white rounded-2xl p-8 border border-gray-100" data-id="element-84">
              <Backpack className="w-12 h-12 text-blue-600 mx-auto mb-4" data-id="element-85" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-86">
                Primaria / Inicial
              </h3>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100" data-id="element-87">
              <GraduationCap className="w-12 h-12 text-blue-600 mx-auto mb-4" data-id="element-88" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-89">
                Secundaria / Media
              </h3>
            </div>
          </div>

          <p className="text-gray-600" data-id="element-90">
            Material separado por nivel y dificultad.
            <br data-id="element-91" />
            Funciona para clases presenciales y virtuales.
          </p>
        </div>
      </section>

      {/* RESULTS */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-92">
        <div className="max-w-4xl mx-auto" data-id="element-93">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1E3A8A] mb-12" data-id="element-94">
            Resultados que vas a notar rápido
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-95">
            <div className="bg-blue-50 rounded-2xl p-8" data-id="element-96">
              <h3 className="text-xl font-semibold text-[#1E3A8A] mb-6" data-id="element-97">
                En tus alumnos:
              </h3>
              <ul className="space-y-4" data-id="element-98">
                {['Entienden el inglés paso a paso', 'Participan más en clase', 'Pierden el miedo a hablar', 'Aprenden de forma más dinámica'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-99">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-100" />
                    <span className="text-gray-700" data-id="element-101">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-green-50 rounded-2xl p-8" data-id="element-102">
              <h3 className="text-xl font-semibold text-green-800 mb-6" data-id="element-103">
                En vos:
              </h3>
              <ul className="space-y-4" data-id="element-104">
                {['Ahorrás horas TODAS las semanas', 'Dejás de improvisar', 'Cubrís el programa completo', 'Das clases más claras y ordenadas', 'Volvés a disfrutar enseñar'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-105">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-106" />
                    <span className="text-gray-700" data-id="element-107">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-108">
        <div className="max-w-4xl mx-auto" data-id="element-109">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1E3A8A] mb-4" data-id="element-110">
            🎁 Bonos incluidos – Sin costo extra
          </h2>
          <p className="text-center text-gray-600 mb-12" data-id="element-111">
            Todo incluido en el acceso.
          </p>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-112">
            {/* Bono 1 - Clave de Respuestas */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center shadow-lg hover:shadow-xl transition-shadow" data-id="element-113">
              <img src="https://cdn.magicpatterns.com/uploads/bU6vfgEiv8GiXc3vjCxTfo/pasted-image.png" alt="Bono 1: Clave de Respuestas" className="w-full max-w-[200px] mx-auto mb-4" loading="lazy" decoding="async" data-id="element-114" />
              <h3 className="font-semibold text-gray-900 mb-2" data-id="element-115">
                Clave de Respuestas
              </h3>
              <p className="text-sm text-gray-600" data-id="element-116">
                Todas las soluciones listas para corregir rápido.
              </p>
            </div>

            {/* Bono 2 - Ebook y Guías PDF */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center shadow-lg hover:shadow-xl transition-shadow" data-id="element-117">
              <img src="https://cdn.magicpatterns.com/uploads/dC8uBntqvQbYCk6qd9Lpn7/pasted-image.png" alt="Bono 2: Ebook y Guías PDF" className="w-full max-w-[200px] mx-auto mb-4" loading="lazy" decoding="async" data-id="element-118" />
              <h3 className="font-semibold text-gray-900 mb-2" data-id="element-119">
                Ebooks y Guías PDF
              </h3>
              <p className="text-sm text-gray-600" data-id="element-120">
                Material complementario para profundizar.
              </p>
            </div>

            {/* Bono 3 - Audios Guías */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center shadow-lg hover:shadow-xl transition-shadow" data-id="element-121">
              <img src="https://cdn.magicpatterns.com/uploads/9H6tCDqXPqbRLrmtGDNCpX/pasted-image.png" alt="Bono 3: Audios Guías" className="w-full max-w-[200px] mx-auto mb-4" loading="lazy" decoding="async" data-id="element-122" />
              <h3 className="font-semibold text-gray-900 mb-2" data-id="element-123">
                Audios para Listening
              </h3>
              <p className="text-sm text-gray-600" data-id="element-124">
                Recursos de audio para pronunciación.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-125">
        <div className="max-w-4xl mx-auto" data-id="element-126">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#1E3A8A] mb-12" data-id="element-127">
            Lo que dicen otros docentes
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-128">
            <TestimonialCard name="Laura Fernández" role="Prof. de Inglés" location="Buenos Aires" quote="Ahora tengo todo el año resuelto. Dejé de pasar los domingos planificando." data-id="element-129" />
            <TestimonialCard name="Carlos Méndez" role="Primaria" location="Córdoba" quote="El material es claro, práctico y adaptable. Mis alumnos participan mucho más." data-id="element-130" />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="purchase" className="py-16 md:py-20 px-4 bg-blue-50" data-id="element-131">
        <div className="max-w-2xl mx-auto" data-id="element-132">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden" data-id="element-133">
            <div className="bg-[#1E3A8A] text-white py-6 px-8 text-center" data-id="element-134">
              <p className="text-blue-200 text-sm font-medium mb-1" data-id="element-135">
                OFERTA ESPECIAL
              </p>
              <h3 className="text-2xl md:text-3xl font-bold" data-id="element-136">
                🇬🇧 MEGAPACK INGLÉS LISTO™
              </h3>
            </div>

            <div className="p-8 md:p-10" data-id="element-137">
              <div className="text-center mb-8" data-id="element-138">
                <p className="text-gray-500 mb-1" data-id="element-139">Precio regular</p>
                <p className="text-2xl text-gray-400 line-through mb-4" data-id="element-140">
                  $49.990 ARS
                </p>
                <p className="text-blue-600 font-semibold text-sm uppercase tracking-wide mb-2" data-id="element-141">
                  Precio hoy
                </p>
                <p className="text-5xl md:text-6xl font-bold text-[#1E3A8A]" data-id="element-142">
                  $19.990 <span className="text-2xl" data-id="element-143">ARS</span>
                </p>
                <p className="text-sm text-gray-500 mt-2" data-id="element-144">(Pesos argentinos)</p>
                <p className="text-green-600 font-semibold mt-4" data-id="element-145">
                  💰 Ahorrás $30.000 ARS
                </p>
              </div>

              <a href="https://checkoutingles.ebookify.online/cart/44892004384815:1" className="block w-full bg-[#2563EB] hover:bg-blue-700 text-white text-xl font-semibold py-5 px-6 rounded-xl text-center shadow-lg hover:shadow-xl transition-all duration-200 mb-6" data-id="element-146">
                👉 ACCEDER AHORA AL MEGAPACK
              </a>

              <p className="text-center text-sm text-gray-500 mb-8" data-id="element-147">
                Acceso inmediato · Descarga digital
              </p>

              <div className="border-t border-gray-100 pt-6" data-id="element-148">
                <p className="font-semibold text-gray-900 mb-4 text-center" data-id="element-149">
                  Lo que recibís hoy:
                </p>
                <ul className="space-y-3" data-id="element-150">
                  {['Acceso completo de por vida', 'Descarga inmediata', 'Material editable y reutilizable', 'Pago 100% seguro', 'Soporte por WhatsApp disponible'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-gray-700" data-id="element-151">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-152" />
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* WhatsApp Support */}
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 md:py-20 px-4 bg-white" data-id="element-153">
        <div className="max-w-2xl mx-auto text-center" data-id="element-154">
          <div className="bg-green-50 rounded-3xl p-8 md:p-12 border border-green-100" data-id="element-155">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-156">
              <Shield className="w-8 h-8 text-green-600" data-id="element-157" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-158">
              Garantía sin riesgo
            </h2>
            <p className="text-lg text-green-700 font-semibold mb-4" data-id="element-159">
              🛡️ Garantía de 7 días
            </p>
            <p className="text-gray-600 mb-4" data-id="element-160">
              Probalo tranquilo/a. Si no te sirve, te devolvemos el 100%.
            </p>
            <p className="text-sm text-gray-500" data-id="element-161">
              Sin letra chica. Todo el riesgo es nuestro.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-20 px-4 bg-gray-50" data-id="element-162">
        <div className="max-w-2xl mx-auto text-center" data-id="element-163">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-6" data-id="element-164">
            ¿Listo para tener todo tu año de Inglés resuelto?
          </h2>
          <a href="https://checkoutingles.ebookify.online/cart/44892004384815:1" className="inline-block bg-[#2563EB] hover:bg-blue-700 text-white text-xl font-semibold py-5 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 mb-4" data-id="element-165">
            🔥 OBTENER MI MEGAPACK INGLÉS LISTO AHORA
          </a>
          <p className="text-sm text-gray-500" data-id="element-166">Acceso digital inmediato</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1E3A8A] text-white py-10 px-4" data-id="element-167">
        <div className="max-w-4xl mx-auto text-center" data-id="element-168">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-blue-200 mb-6" data-id="element-169">
            <span className="flex items-center gap-1" data-id="element-170">
              <Shield className="w-4 h-4" data-id="element-171" />
              Pago seguro
            </span>
            <span data-id="element-172">·</span>
            <span className="flex items-center gap-1" data-id="element-173">
              <Download className="w-4 h-4" data-id="element-174" />
              Acceso inmediato
            </span>
            <span data-id="element-175">·</span>
            <span data-id="element-176">Producto digital</span>
          </div>
          <p className="text-xs text-blue-300" data-id="element-177">
            Este sitio no está afiliado ni patrocinado por Facebook.
          </p>
          <p className="text-xs text-blue-400 mt-2" data-id="element-178">
            © 2024 Megapack Inglés Listo. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}